import { SurveyBehavesAs } from './Survey.type';

//

/** ─────────────────────────────
 *  Root record
 * ─────────────────────────────*/

/** ─────────────────────────────
 *  Summary
 * ─────────────────────────────*/
export enum ComplaintSummaryType {
  Initial = 'initial',
  Interim = 'interim',
  Reeval = 'reeval',
}

export interface ComplaintSummary {
  id?: string;
  complaintId: string;
  complaintRelatedQuestions: Record<string, QuestionAnswer>;
  createdAt: number;
  /** Source of this complaint summary */
  type?: ComplaintSummaryType;
  hide?: boolean;
  createComplaint?: boolean;
  surveyId?: string;
  /** Minimal shell indicator: true until survey completion populates answers */
  placeholder?: boolean;
  patientSurveyId?: string;
  clinicId: number;
  patientId: string;
  encounterId: string;
  updatedAt: number;
  trackId?: number;
  behavesAs?: SurveyBehavesAs | null;
  subjectiveTemplate?: string;
  /** Maps friendly template aliases to question UUIDs for template resolution. */
  templateAliasMap?: Record<string, string>;
}

/** ─────────────────────────────
 *  Sections & items
 * ─────────────────────────────*/

/**
 * Frontend rendering schema for flexible summaries
 * These types are consumed by UI helpers (e.g., complaintSummaryMaps.ts)
 */

/** Looser conditional type to support null/optional conditions in data */
export interface IfConditionLoose {
  id?: string;
  value?: unknown; // NULL | string | number | boolean | …
}

export interface SummaryTextItem {
  type: 'text';
  /** Free-form template string containing token placeholders */
  template?: string;
  /** Map of question-ID → answer block */
  questionAnswerMap?: Record<string, QuestionAnswer>;
  /** Optional conditional (appears as "if" in data) */
  if?: IfConditionLoose;
  /** Optional flag to hide this item in the frontend UI */
  hide?: boolean;
  /** Optional flag to indicate if this item is shared across all complaints */
  shared?: boolean;
}

export interface SummarySection {
  type: 'section' | string;
  title?: string;
  /** Optional flag to hide this section in the frontend UI */
  hide?: boolean;
  /** Optional main QA map for section-level questions */
  mainQuestionAnswerMap?: Record<string, QuestionAnswer>;
  /** Nested items */
  items?: SummaryItem[];
  /** Optional conditional for section */
  if?: IfConditionLoose;
  /** Optional shared flag */
  shared?: boolean;
  /** Optional QA map directly on the section */
  questionAnswerMap?: Record<string, QuestionAnswer>;
}

/** Union so you can add new item types later */
export type SummaryItem = SummaryTextItem | SummarySection;

/** Shown in every section of your sample */
export interface TextItem {
  type: string;
  /** Free-form template string containing token placeholders */
  template: string;
  /** Map of question-ID → answer block */
  questionAnswerMap?: Record<string, QuestionAnswer>;
  /**
   * Optional conditional (appears as `"if"` in data).
   * “value” can be NULL, string, number, etc.—use `unknown` or
   * make a more specific discriminated union if needed.
   */
  if?: IfCondition;
  /** Optional flag to hide this item in the frontend UI */
  hide?: boolean;
  /** Optional flag to indicate if this item is shared across all complaints */
  shared?: boolean;
}

/** ─────────────────────────────
 *  Question / answer details
 * ─────────────────────────────*/
/** Slider range/labels carried from the survey question's typeOptions so inline
 *  editing can honor a custom (non-CCS) slider's configuration. */
export interface SliderDisplayConfig {
  min?: number;
  max?: number;
  step?: number;
  left?: string;
  right?: string;
}

export interface QuestionAnswer {
  questionText: string;
  /** One of the built-in control types you’re using */
  questionType: QuestionType;
  /** Values actually selected by the patient */
  selectedValues: string[];
  /** All possible answer options rendered to the user */
  options: QuestionOption[];
  /** Custom text when "Other" option is selected */
  otherValue?: string;
  /** Custom input data for askForInput functionality */
  customInputData?: Record<string, string>;
  /** For group questions: each sub-question's resolved answer, in order. */
  groupSubAnswers?: (QuestionAnswer & { id: string })[];
  /** Slider config from the question definition; used by inline edit controls. */
  sliderConfig?: SliderDisplayConfig;
  shared?: boolean;
  complaintId?: string;
  /** Survey position for display ordering (see UnifiedQAEntry). */
  order?: number;
}

export type QuestionType =
  | 'radio'
  | 'checkbox'
  | 'slider'
  | 'date'
  | 'combobox'
  | 'input'
  | 'textarea'
  | 'checkboxWithInputs'
  | 'group'
  | 'bodypicker';

export interface QuestionOption {
  id: string;
  display: string;
  selected: boolean;
}

/** ─────────────────────────────
 *  Conditional helper
 * ─────────────────────────────*/
export interface IfCondition {
  id: string;
  value: unknown; // NULL | string | number | boolean | …
}

export type ComplaintSummaryScope = Pick<
  ComplaintSummary,
  'clinicId' | 'encounterId' | 'complaintId'
>;
